import React, { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api";
import { motion } from "framer-motion";
import {
  BarChart3, Loader2, TrendingUp, TrendingDown, ShieldCheck, ShieldAlert, ShieldX,
  Gauge, Sparkles, Users, ArrowUpRight, ArrowDownRight, Minus,
} from "lucide-react";

const REL_STYLE = {
  high: { bg: "rgba(34,197,94,0.14)", fg: "#16a34a", Icon: ShieldCheck },
  medium: { bg: "rgba(245,158,11,0.16)", fg: "#b45309", Icon: ShieldAlert },
  none: { bg: "rgba(148,163,184,0.14)", fg: "#64748b", Icon: ShieldX },
};

const PRESSURE_STYLE = {
  high: { bg: "rgba(239,68,68,0.14)", fg: "#ef4444" },
  medium: { bg: "rgba(245,158,11,0.16)", fg: "#b45309" },
  low: { bg: "rgba(34,197,94,0.14)", fg: "#16a34a" },
};

export default function MarketBenchmark({ propId, lang = "it", date }) {
  const L = (it, en) => (lang === "en" ? en : it);
  const [bench, setBench] = useState(null);
  const [explain, setExplain] = useState(null);
  const [loading, setLoading] = useState(true);
  const [aiLoading, setAiLoading] = useState(true);

  const load = useCallback(async () => {
    if (!propId) return;
    setLoading(true);
    try { const { data } = await api.get(`/properties/${propId}/market-benchmark?lang=${lang}`); setBench(data); }
    catch { setBench(null); }
    finally { setLoading(false); }
  }, [propId, lang]);

  const loadExplain = useCallback(async () => {
    if (!propId) return;
    setAiLoading(true);
    try { const { data } = await api.get(`/properties/${propId}/pricing-explanation?date=${date || ""}&lang=${lang}`); setExplain(data); }
    catch { setExplain(null); }
    finally { setAiLoading(false); }
  }, [propId, date, lang]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadExplain(); }, [loadExplain]);

  const rel = bench?.reliability || "none";
  const relS = REL_STYLE[rel] || REL_STYLE.none;
  const mp = explain?.market_pressure;
  const mpS = PRESSURE_STYLE[mp?.level] || PRESSURE_STYLE.low;
  const own = bench?.your_metrics || {};

  const MetricPill = ({ label, value, sub, testid }) => (
    <div className="bg-muted/40 rounded-lg p-3" data-testid={testid}>
      <p className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground truncate">{label}</p>
      <p className="font-data text-xl font-semibold mt-0.5">{value}</p>
      {sub && <p className="text-[11px] text-muted-foreground">{sub}</p>}
    </div>
  );

  const fmt = (v, unit) => (v == null ? "—" : `${unit === "€" ? "€" : ""}${Math.round(v)}${unit === "%" ? "%" : unit === "gg" ? "gg" : ""}`);

  return (
    <motion.div data-testid="market-benchmark" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
      className="md:col-span-4 bg-card border border-border rounded-lg p-6">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <BarChart3 className="w-5 h-5 text-primary" />
          <h3 className="text-lg font-semibold">{L("Benchmark di Mercato · La mia struttura", "Market Benchmark · My property")}</h3>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {bench && (
            <span className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full font-medium" style={{ backgroundColor: relS.bg, color: relS.fg }} data-testid="mb-reliability">
              <relS.Icon className="w-3.5 h-3.5" />
              {rel === "high" ? L("Alta affidabilità", "High reliability") : rel === "medium" ? L("Bassa affidabilità", "Low reliability") : L("Benchmark non disponibile", "Benchmark unavailable")}
            </span>
          )}
          {bench?.structure_count != null && (
            <span className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full bg-muted text-muted-foreground" data-testid="mb-count">
              <Users className="w-3.5 h-3.5" />{L(`${bench.structure_count} strutture`, `${bench.structure_count} structures`)}
            </span>
          )}
          {mp?.index != null && (
            <span className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full font-medium" style={{ backgroundColor: mpS.bg, color: mpS.fg }} data-testid="mb-pressure-badge">
              <Gauge className="w-3.5 h-3.5" />{L("Pressione mercato", "Market pressure")} {mp.index}/100
            </span>
          )}
        </div>
      </div>

      {loading ? (
        <div className="py-8 text-center text-muted-foreground text-sm"><Loader2 className="w-5 h-5 animate-spin inline mr-2" />{L("Calcolo benchmark…", "Computing benchmark…")}</div>
      ) : (
        <div className="space-y-6">
          {/* Your forward-looking metrics */}
          <div>
            <p className="text-xs uppercase tracking-widest font-bold text-muted-foreground mb-2">{L("Le tue metriche", "Your metrics")}</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <MetricPill testid="mb-fwd-occ" label={L("Forward Occupancy", "Forward Occupancy")} value={own.forward_occupancy_index != null ? `${own.forward_occupancy_index}%` : "—"}
                sub={own.forward_occupancy_prev_year != null ? L(`Anno prec. ${own.forward_occupancy_prev_year}%`, `Prev year ${own.forward_occupancy_prev_year}%`) : L("occupazione futura sui libri", "future on-the-books")} />
              <MetricPill testid="mb-pickup-daily" label={L("Pickup 24h", "24h pickup")} value={own.pickup_daily != null ? own.pickup_daily : "—"} sub={L("camere/notti vendute", "room-nights sold")} />
              <MetricPill testid="mb-pickup-weekly" label={L("Pickup 7gg", "7-day pickup")} value={own.pickup_weekly != null ? own.pickup_weekly : "—"}
                sub={own.pickup_weekly_prev_year != null ? L(`Anno prec. ${own.pickup_weekly_prev_year}`, `Prev year ${own.pickup_weekly_prev_year}`) : ""} />
              <MetricPill testid="mb-adr" label="ADR" value={own.adr != null ? `€${Math.round(own.adr)}` : "—"} sub={own.revpar != null ? `RevPAR €${Math.round(own.revpar)}` : ""} />
            </div>
          </div>

          {/* YoY */}
          {(bench?.yoy || []).length > 0 && (
            <div data-testid="mb-yoy">
              <p className="text-xs uppercase tracking-widest font-bold text-muted-foreground mb-2">{L("Confronto anno precedente", "Year-over-year")}</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {bench.yoy.map((y, i) => {
                  const up = (y.delta_pct || 0) > 0;
                  return (
                    <div key={i} className="flex items-center justify-between bg-muted/40 rounded-lg p-3">
                      <div>
                        <p className="text-sm font-medium">{y.label}</p>
                        <p className="text-xs text-muted-foreground">{L("Ora", "Now")} {y.current}{y.unit} · {L("Anno prec.", "Prev")} {y.previous}{y.unit}</p>
                      </div>
                      {y.delta_pct != null && (
                        <span className="inline-flex items-center gap-1 text-sm font-semibold" style={{ color: up ? "#16a34a" : y.delta_pct < 0 ? "#ef4444" : "#64748b" }}>
                          {up ? <ArrowUpRight className="w-4 h-4" /> : y.delta_pct < 0 ? <ArrowDownRight className="w-4 h-4" /> : <Minus className="w-4 h-4" />}
                          {y.delta_pct > 0 ? "+" : ""}{y.delta_pct}%
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Benchmark comparison vs market */}
          <div>
            <p className="text-xs uppercase tracking-widest font-bold text-muted-foreground mb-2">{L("Tu vs mercato (mediana anonima)", "You vs market (anonymous median)")}</p>
            {!bench?.available ? (
              <p className="text-sm text-muted-foreground bg-muted/40 rounded-lg p-4" data-testid="mb-unavailable">{bench?.message || L("Benchmark non ancora disponibile.", "Benchmark not available yet.")}</p>
            ) : (
              <>
                {bench.warning && <p className="text-xs mb-3 px-3 py-2 rounded-md bg-amber-500/10 text-amber-700 dark:text-amber-400" data-testid="mb-warning">{bench.warning}</p>}
                <div className="space-y-2" data-testid="mb-insights">
                  {bench.insights.map((row, i) => {
                    const above = (row.delta_pct || 0) > 0;
                    return (
                      <div key={i} className="flex items-center justify-between gap-3 border-b border-border/50 pb-2">
                        <span className="text-sm font-medium w-40 shrink-0">{row.label}</span>
                        <div className="flex-1 flex items-center gap-3 text-sm">
                          <span className="text-muted-foreground">{L("Tu", "You")}: <span className="font-data font-semibold text-foreground">{fmt(row.your_value, row.unit)}</span></span>
                          <span className="text-muted-foreground">{L("Mercato", "Market")}: <span className="font-data">{fmt(row.market_median, row.unit)}</span></span>
                        </div>
                        {row.delta_pct != null && (
                          <span className="inline-flex items-center gap-1 text-xs font-semibold shrink-0" style={{ color: above ? "#16a34a" : row.delta_pct < 0 ? "#ef4444" : "#64748b" }}>
                            {above ? <TrendingUp className="w-3.5 h-3.5" /> : <TrendingDown className="w-3.5 h-3.5" />}{row.delta_pct > 0 ? "+" : ""}{row.delta_pct}%
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>

          {/* Market pressure breakdown */}
          {mp?.components && (
            <div data-testid="mb-pressure">
              <p className="text-xs uppercase tracking-widest font-bold text-muted-foreground mb-2">{L("Pressione di mercato (Market Pressure Index)", "Market Pressure Index")}</p>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                {mp.components.map((c) => (
                  <div key={c.key} className="bg-muted/40 rounded-lg p-2.5" title={c.detail}>
                    <p className="text-[10px] uppercase tracking-wide font-bold text-muted-foreground truncate">{c.label}</p>
                    <p className="font-data text-lg font-semibold">{c.value}</p>
                    <div className="mt-1 h-1.5 rounded-full bg-secondary overflow-hidden">
                      <div className="h-full rounded-full" style={{ width: `${c.value}%`, backgroundColor: mpS.fg }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* AI natural-language explanation */}
          <div className="border-2 border-primary/25 rounded-lg p-4" data-testid="mb-ai">
            <div className="flex items-center gap-2 mb-2"><Sparkles className="w-4 h-4 text-primary" /><h4 className="text-sm font-semibold">{L("Perché questo prezzo (AI)", "Why this price (AI)")}</h4></div>
            {aiLoading ? (
              <p className="text-sm text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin inline mr-1.5" />{L("L'AI sta analizzando la tua struttura…", "AI is analyzing your property…")}</p>
            ) : explain?.ai ? (
              <div>
                {explain.ai.headline && <p className="font-semibold text-sm mb-1" data-testid="mb-ai-headline">{explain.ai.headline}</p>}
                {explain.ai.explanation && <p className="text-sm text-muted-foreground">{explain.ai.explanation}</p>}
                {(explain.ai.actions || []).length > 0 && (
                  <ul className="mt-2 space-y-1">
                    {explain.ai.actions.map((a, i) => (
                      <li key={i} className="text-sm flex gap-2"><span className="text-primary">→</span>{a}</li>
                    ))}
                  </ul>
                )}
              </div>
            ) : <p className="text-sm text-muted-foreground">—</p>}
          </div>
        </div>
      )}
    </motion.div>
  );
}
